import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Compass, FileSignature, ShieldCheck, Briefcase, Building2, Users, Sparkles, ClipboardList, MessageSquare, Wallet, ScrollText } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * Dynamic user guide. The page tailors itself to the user's EFFECTIVE role
 * (which means it instantly updates when a system_admin uses the role
 * impersonator). Zero LLM cost — content is static per role.
 */

const GUIDES = {
  system_admin: {
    headline: "Platform owner playbook",
    intro: "You see every tenant, every audit event, every workflow. Use the Audit Log super-view and the Role Impersonator to QA each role's experience without leaving your account.",
    steps: [
      { icon: ScrollText, label: 'Audit Log (cross-tenant)', to: '/dashboard/audit', desc: 'Filter every action across every tenant. Use the tenant filter chip to drill down.' },
      { icon: ShieldCheck, label: 'Security & DR', to: '/dashboard/security', desc: 'Confirm Dawud middleware is live, last Harun threat scan, and that backups are encrypting.' },
      { icon: Sparkles, label: 'AI Employees', to: '/dashboard/ai-employees', desc: '15 AI advisors — 7 leadership, 4 specialists, 2 security, 2 mentors. All ages displayed.' },
      { icon: BookOpen, label: 'AI Mentors', to: '/dashboard/ai-mentors', desc: "Shu'aib & Idris dashboard. Run 'Train Now' to age every AI by +10 years on demand." },
      { icon: Building2, label: 'Tenant settings', to: '/dashboard/tenant', desc: 'White-label brand, invite admins, view subscription. Only platform owner can resell.' },
    ],
    glossary: [
      ['Holding admin', 'Tenant-level super-approver. Owns one tenant.'],
      ['Impersonation', 'X-Impersonate-Role header swaps role for one request. Audit-logged.'],
      ['Curriculum cycle', '+10y of accumulated mastery applied to every AI. Daily 04:00 UTC.'],
    ],
  },
  holding_admin: {
    headline: "Run your holding company end-to-end",
    intro: "You are the operating heart of your tenant. The Approval Center, Board Minutes, and Entity Portfolio are your daily three.",
    steps: [
      { icon: ShieldCheck, label: 'Approval Center', to: '/dashboard/approvals', desc: 'Every change-request waiting on you. Items waiting 3+ days are auto-escalated.' },
      { icon: Building2, label: 'Entity Portfolio', to: '/dashboard/entities-os', desc: "Every Ruʾya — your holding's entities, certificates, expiries." },
      { icon: ClipboardList, label: 'Board Minutes', to: '/dashboard/minutes', desc: 'Record meeting minutes, track action items, export as PDF.' },
      { icon: FileSignature, label: 'NDA & MOU', to: '/dashboard/agreements', desc: 'Draft, send, and sign — Maryam (AI Lawyer) can pre-draft any clause.' },
      { icon: Briefcase, label: 'Opportunities CRM', to: '/dashboard/opportunities', desc: 'Inbound / outbound pipeline. Tag stage, owner, expected value.' },
    ],
    glossary: [
      ['PTO mode', 'Pauses your daily approver digest while on leave. Toggle in the sidebar.'],
      ['Escalation', 'Items waiting 3+ days are also CC\'d up the chain (legal → cfo → ceo → chairman).'],
    ],
  },
  chairman: {
    headline: "Strategic oversight & final approval",
    intro: "You are the chain's last word. Items reach you only after legal/cfo/ceo have processed.",
    steps: [
      { icon: ShieldCheck, label: 'Approval Center', to: '/dashboard/approvals', desc: 'Decide on the items that reached the chairman step.' },
      { icon: ClipboardList, label: 'Board Minutes', to: '/dashboard/minutes', desc: 'Read this week\'s board summary and weigh in on action items.' },
      { icon: MessageSquare, label: 'AI Council', to: '/dashboard/ai-council', desc: 'Convene up to 5 AI advisors before a tough call.' },
    ],
    glossary: [
      ['Step chain', 'legal → cfo → ceo → chairman. You see only your step.'],
    ],
  },
  ceo: {
    headline: "Execute on strategy",
    intro: "Your dashboard centres on Approvals, the AI Council, and the weekly Board Brief that Yusuf composes for you every Monday.",
    steps: [
      { icon: ShieldCheck, label: 'Approval Center', to: '/dashboard/approvals', desc: 'CEO/CFO-step decisions land here.' },
      { icon: Sparkles, label: 'AI Employees', to: '/dashboard/ai-employees', desc: 'Yusuf (AI CEO) drafts the Monday board brief automatically.' },
      { icon: Briefcase, label: 'Opportunities', to: '/dashboard/opportunities', desc: 'CRM-level pipeline visibility.' },
    ],
    glossary: [
      ['Yusuf board brief', 'Auto-generated every Monday 07:00 UTC for the primary tenant.'],
    ],
  },
  cfo: {
    headline: "Run the numbers",
    intro: "Bank settings, invoice drafts, and pipeline QAR — all in one place. Khadija (AI CFO) can model any scenario you ask.",
    steps: [
      { icon: Wallet, label: 'Bank & Billing', to: '/dashboard/billing/bank', desc: 'Bank accounts, subscription state, customer-portal link.' },
      { icon: Sparkles, label: 'AI Employees', to: '/dashboard/ai-employees', desc: 'Khadija drafts invoices, models runway, scores unit economics.' },
      { icon: ShieldCheck, label: 'Approvals', to: '/dashboard/approvals', desc: 'CFO-step approvals waiting on you.' },
    ],
    glossary: [
      ['Khadija', 'AI CFO — speaks CAC, LTV, gross margin, burn multiple, Rule of 40.'],
    ],
  },
  legal: {
    headline: "Draft fast, never miss a clause",
    intro: "Maryam (AI Lawyer) drafts a 5-10 clause agreement in one click. Every escalation flows through you first.",
    steps: [
      { icon: FileSignature, label: 'NDA & MOU', to: '/dashboard/agreements', desc: 'Drafts pre-vetted by Maryam. Send-for-signing in one click.' },
      { icon: ShieldCheck, label: 'Approval Center', to: '/dashboard/approvals', desc: 'Legal-step decisions are your queue.' },
      { icon: Sparkles, label: 'AI Drafts', to: '/dashboard/ai-drafts', desc: 'Bulk drafts library — Maryam\'s output saved for review.' },
    ],
    glossary: [
      ['Maryam', 'AI Lawyer — GCC corporate law, NDAs, MOUs, SPAs, SHAs.'],
    ],
  },
  investor: {
    headline: "See the vetted opportunities",
    intro: "You see startups that passed Vigilant Entities\' AI vetting, plus the Investor Portal for deal flow.",
    steps: [
      { icon: Users, label: 'Investor Portal', to: '/dashboard/investors', desc: 'All vetted startups with score, stage, and decks.' },
      { icon: MessageSquare, label: 'Compliance Chat', to: '/dashboard/chat', desc: 'Direct line with founders and admin.' },
    ],
    glossary: [],
  },
  founder: {
    headline: "Founder journey — 6 gates",
    intro: "You must complete the founder journey in order: Profile → Pitch deck → AI vetting → Psychometric → NDA → MOU. Gates are server-side, so direct API calls won't bypass them.",
    steps: [
      { icon: Compass, label: 'Founder Journey', to: '/dashboard/founder', desc: 'Your six gates with a green checkmark per step.' },
      { icon: MessageSquare, label: 'Compliance Chat', to: '/dashboard/chat', desc: 'Direct line with the admin team.' },
    ],
    glossary: [
      ['Gate', 'A server-side check enforcing the founder workflow. Cannot be skipped.'],
      ['Psychometric', 'Required AFTER AI vetting and BEFORE drafting any agreement.'],
    ],
  },
  startup_member: {
    headline: "Support the founder",
    intro: "You have read access to the founder\'s startup record. All agreements and signings are routed through the founder.",
    steps: [
      { icon: Compass, label: 'Founder Journey', to: '/dashboard/founder', desc: 'Read-only — see where the founder is in the journey.' },
    ],
    glossary: [],
  },
  guest: {
    headline: "Welcome — start your founder journey",
    intro: "You are pre-vetting. Begin by creating your startup profile and uploading your pitch deck.",
    steps: [
      { icon: Compass, label: 'Founder Journey', to: '/dashboard/founder', desc: 'Start here.' },
    ],
    glossary: [],
  },
};

export default function UserGuides() {
  const { effectiveRole, isImpersonating, realRole } = useAuth();
  const role = effectiveRole || 'guest';
  const guide = useMemo(() => GUIDES[role] || GUIDES.guest, [role]);

  return (
    <div className="ve-section" data-testid="user-guides-page">
      <div className="ve-eyebrow flex items-center gap-2">
        <BookOpen size={14} className="text-[#8B3FC5]"/>
        Help Center
      </div>
      <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
        {guide.headline}
        {isImpersonating && (
          <span className="ml-3 text-xs uppercase tracking-[0.2em] text-[#5E1F8B]">· preview as {role}</span>
        )}
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-3xl">{guide.intro}</p>

      <div className="mt-8">
        <div className="ve-eyebrow text-[#5E1F8B]">Your most common tasks</div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {guide.steps.map((s, i) => (
            <Link
              key={s.to || s.label}
              to={s.to}
              data-testid={`guide-step-${i}`}
              className="ve-card p-4 hover:border-[#8B3FC5] transition"
            >
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                  <s.icon size={16} className="text-[#8B3FC5]"/>
                </div>
                <div className="text-sm font-bold">{s.label}</div>
              </div>
              <p className="mt-2 text-xs text-slate-600 dark:text-white/70 leading-snug">{s.desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {guide.glossary?.length > 0 && (
        <div className="mt-8">
          <div className="ve-eyebrow text-[#5E1F8B]">Glossary</div>
          <div className="mt-3 ve-card p-4 divide-y divide-slate-100 dark:divide-white/10">
            {guide.glossary.map(([term, def]) => (
              <div key={term} className="py-2 first:pt-0 last:pb-0">
                <div className="text-sm font-bold">{term}</div>
                <div className="text-xs text-slate-600 dark:text-white/70 mt-0.5">{def}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 text-xs text-slate-500">
        Effective role: <b className="text-slate-700 dark:text-white capitalize">{role.replace(/_/g, ' ')}</b>
        {isImpersonating && (<> · Real role: <b className="text-slate-700 dark:text-white capitalize">{realRole?.replace(/_/g, ' ')}</b></>)}
      </div>
    </div>
  );
}
